import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './App-additional.css';
import './App.css';
import headerLogo from './assets/차트트레이너.png';
import AuthModal from './components/AuthModal';
import ConsentModal from './components/ConsentModal';
import { GameMain } from './components/game/GameMain';
import { LandingPage } from './components/landing/LandingPage';
import { TopInfoBar } from './components/layout/TopInfoBar';
import { PrivacyModal } from './components/legal/PrivacyModal';
import { TermsModal } from './components/legal/TermsModal';
import { MyPagePanel } from './components/mypage/MyPagePanel';
import { RankingPanel } from './components/ranking/RankingPanel';
import { API_BASE_URL } from './config/api';
import { TradingHistoryPanel } from './features/trading-history';
import { useAuth } from './hooks/useAuth';
import { useGameState } from './hooks/useGameState';
import { getMyPageData } from './services/mypageService';
import { getCachedMyRank, type UserRankWithBadge } from './services/userRankService';

// 로그에서 URL 제거 (민감 정보 보호)

function App() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showRankingModal, setShowRankingModal] = useState(false);
  const [showTradingHistoryModal, setShowTradingHistoryModal] = useState(false);
  const [showMyPageModal, setShowMyPageModal] = useState(false);
  const [myRank, setMyRank] = useState<UserRankWithBadge | null>(null);
  const [myRankFlashToken, setMyRankFlashToken] = useState(0);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivateProfilePopup, setShowPrivateProfilePopup] = useState(false);
  const [showViewerPrivateProfilePopup, setShowViewerPrivateProfilePopup] =
    useState(false);
  const [highlightPublicProfile, setHighlightPublicProfile] = useState(false);
  const [selectedUserKey, setSelectedUserKey] = useState<number | undefined>(
    undefined
  );
  const [selectedNickname, setSelectedNickname] = useState<string | undefined>(
    undefined
  );
  // 매매내역 화면 진입 경로 추적 ('mypage' | 'topbar' | 'ranking' | null)
  const [tradingHistorySource, setTradingHistorySource] = useState<
    'mypage' | 'topbar' | 'ranking' | null
  >(null);
  // 카카오 리다이렉트 복귀 시 랜딩이 잠깐 보이는 것 방지 (URL에 code 있으면 로딩 화면)
  const [isKakaoCallbackPending, setIsKakaoCallbackPending] = useState(
    () =>
      typeof window !== 'undefined' &&
      !!new URLSearchParams(window.location.search).get('code')
  );

  const {
    user,
    accessToken,
    pendingSignupData,
    login: loginUser,
    register: registerUser,
    loginWithKakao: loginWithKakaoUser,
    logout: logoutUser,
    refreshAccessToken,
    updateUser,
    handleKakaoCallback,
    completeSignup,
    cancelSignup,
  } = useAuth();

  const game = useGameState({
    apiBaseUrl: API_BASE_URL,
    user,
    accessToken,
    refreshAccessToken,
    updateUser,
  });

  const { state, actions, helpers } = game;
  const { toggleTheme, loadRankings, changeRankingPage } = actions;

  // 인증 만료 이벤트 처리
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('🔐 인증 만료 감지 - 로그인 모달 표시');
      setShowAuthModal(true);
      logoutUser(); // 로그아웃 처리
    };

    window.addEventListener('auth-expired', handleAuthExpired);

    return () => {
      window.removeEventListener('auth-expired', handleAuthExpired);
    };
  }, [logoutUser]);

  // 비밀번호 재설정 토큰 처리 (URL에서 token 파라미터 확인)
  useEffect(() => {
    const checkToken = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');

      if (token) {
        console.log('🔑 App.tsx: 비밀번호 재설정 토큰 감지, 모달 열기');
        // 토큰이 있으면 모달 열기 (AuthModal에서 resetPassword 모드로 자동 전환됨)
        // URL에서 토큰 제거는 AuthModal에서 처리하도록 함
        setShowAuthModal(true);
      }
    };

    // 초기 로드 시 확인
    checkToken();

    // URL 변경 감지를 위한 이벤트 리스너 (popstate는 브라우저 뒤로가기/앞으로가기)
    window.addEventListener('popstate', checkToken);

    return () => {
      window.removeEventListener('popstate', checkToken);
    };
  }, []);

  // 카카오 로그인 콜백 처리 (중복 실행 방지)
  const kakaoCallbackProcessed = useRef(false);
  useEffect(() => {
    // 이미 처리되었으면 무시
    if (kakaoCallbackProcessed.current) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const isKakaoCallback =
      window.location.pathname.includes('/auth/kakao/callback') || code;

    if (isKakaoCallback && code) {
      // 즉시 처리 중 플래그 설정 및 URL을 루트로 변경 (중복 방지)
      kakaoCallbackProcessed.current = true;
      // 콜백 URL을 루트 경로로 변경 (기본 URL로 복귀)
      window.history.replaceState({}, document.title, '/');

      (async () => {
        try {
          // AuthContext의 handleKakaoCallback 사용 (올바른 인증 처리)
          await handleKakaoCallback(code);
          // 모달은 user 반영 후 useEffect에서 닫음; 여기서는 로딩 해제만
          setIsKakaoCallbackPending(false);
        } catch (error) {
          console.error('카카오 로그인 콜백 처리 실패:', error);
          setShowAuthModal(true);
          setIsKakaoCallbackPending(false);
          kakaoCallbackProcessed.current = false;
        }
      })();
    }
  }, [handleKakaoCallback]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      await loginUser(email, password);
      // 모달은 user 반영 후 useEffect에서 닫음 (랜딩이 잠깐 보이는 현상 방지)
    },
    [loginUser]
  );

  const handleRegister = useCallback(
    async (email: string, password: string, nickname: string) => {
      await registerUser(email, password, nickname);
      // 모달은 user 반영 후 useEffect에서 닫음 (랜딩이 잠깐 보이는 현상 방지)
    },
    [registerUser]
  );

  // 로그인/회원가입 성공 시 user가 반영된 뒤에만 모달 닫기 (랜딩이 0.3초 보이는 현상 방지)
  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  const handleKakaoLogin = useCallback(async () => {
    await loginWithKakaoUser();
    // 카카오 로그인은 리다이렉트되므로 여기서는 모달을 닫지 않음
  }, [loginWithKakaoUser]);

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const handleLogout = useCallback(() => {
    setShowLogoutConfirm(true);
  }, []);

  const handleLogoutConfirm = useCallback(async () => {
    // 게임이 진행 중이면 먼저 게임 종료 처리
    if (game.state.gameState.isGameActive) {
      try {
        await game.actions.endGame();
        console.log('✅ 로그아웃 전 게임 종료 완료');
      } catch (error) {
        console.error('❌ 로그아웃 전 게임 종료 실패:', error);
        // 게임 종료 실패해도 로그아웃은 진행
      }
    }

    logoutUser();
    setShowRankingModal(false);
    setShowTradingHistoryModal(false);
    setShowMyPageModal(false); // 마이페이지 모달도 닫기
    setShowLogoutConfirm(false);
    setShowAuthModal(true); // 로그인 화면으로 이동
  }, [logoutUser, game]);

  const handleLogoutCancel = useCallback(() => {
    setShowLogoutConfirm(false);
  }, []);

  const handleToggleRanking = useCallback(() => {
    if (showRankingModal) {
      // 이미 랭킹 화면이면 게임 화면으로 돌아가기
      setShowRankingModal(false);
    } else {
      // 다른 화면에서 랭킹 화면으로 전환 (다른 모달들 모두 닫기)
      setShowTradingHistoryModal(false);
      setShowMyPageModal(false);
      setShowRankingModal(true);
      loadRankings();
    }
  }, [showRankingModal, loadRankings]);

  const handleToggleTradingHistory = useCallback(() => {
    if (!user) {
      // 로그인하지 않은 경우 로그인 모달 표시
      setShowAuthModal(true);
      return;
    }

    if (showTradingHistoryModal) {
      // 이미 매매내역 화면이면 게임 화면으로 돌아가기
      setShowTradingHistoryModal(false);
      setSelectedUserKey(undefined); // 선택된 사용자 초기화
      setSelectedNickname(undefined); // 선택된 닉네임 초기화
      setTradingHistorySource(null);
    } else {
      // 다른 화면에서 매매내역 화면으로 전환 (탑바에서 진입)
      setShowRankingModal(false);
      setShowMyPageModal(false); // 마이페이지 모달도 닫기
      setShowTradingHistoryModal(true);
      setSelectedUserKey(undefined); // 기본적으로 현재 사용자의 매매내역 표시
      setSelectedNickname(undefined);
      setTradingHistorySource('topbar');
    }
  }, [showTradingHistoryModal, user]);

  const handleAccountClick = useCallback(
    async (userKey: number, nickname: string, isPublicProfile: boolean) => {
      // 랭킹에서 계정 클릭 시 해당 계정의 매매내역 표시
      // 조회 대상이 비공개 프로필인 경우 팝업 표시
      if (!isPublicProfile) {
        setShowPrivateProfilePopup(true);
        return;
      }

      // 현재 사용자(조회자)의 프로필 공개 여부 확인
      if (user) {
        try {
          const myPageData = await getMyPageData(API_BASE_URL);
          if (!myPageData.user.isPublicProfile) {
            // 조회자가 비공개 프로필이면 다른 사용자의 매매내역을 볼 수 없음
            setShowViewerPrivateProfilePopup(true);
            return;
          }
        } catch (error) {
          console.error('마이페이지 데이터 조회 실패:', error);
          // 에러가 발생해도 계속 진행 (백엔드에서도 체크하므로)
        }
      }

      console.log('상태 변경:', {
        showRankingModal: false,
        showTradingHistoryModal: true,
        selectedUserKey: userKey,
        tradingHistorySource: 'ranking',
      });
      setShowRankingModal(false);
      setShowTradingHistoryModal(true);
      setSelectedUserKey(userKey);
      setSelectedNickname(nickname);
      setTradingHistorySource('ranking'); // 랭킹에서 진입
    },
    [user]
  );

  const handleGoHome = useCallback(() => {
    // 모든 모달을 닫고 게임 화면으로 돌아가기
    setShowRankingModal(false);
    setShowTradingHistoryModal(false);
    setShowMyPageModal(false);
    setSelectedUserKey(undefined);
    setSelectedNickname(undefined);
    setTradingHistorySource(null);
  }, []);

  const handleViewMyRank = useCallback(async () => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    const result = await getCachedMyRank(API_BASE_URL, true);
    if (!result) {
      return;
    }
    setMyRank(result);
    setMyRankFlashToken(Date.now());

    // Top 50 안이면 해당 페이지로 이동 (itemsPerPage=10)
    if (result.rank >= 1 && result.rank <= 50) {
      const page = Math.floor((result.rank - 1) / 10) + 1;
      changeRankingPage(page);
    }
  }, [user, changeRankingPage, API_BASE_URL]);

  const themeClass = useMemo(
    () => (state.theme === 'light' ? 'light-theme' : 'dark-theme'),
    [state.theme]
  );

  // 로그인하지 않은 사용자에게 랜딩 페이지 표시 (카카오 콜백 처리 중이거나 모달 열림 시 제외)
  const showLandingPage =
    !user &&
    !showAuthModal &&
    !showRankingModal &&
    !showTradingHistoryModal &&
    !showMyPageModal &&
    !isKakaoCallbackPending;
  
  // 회원가입 모드로 모달을 열지 결정
  const [authModalMode, setAuthModalMode] = useState<'login' | 'register'>('login');

  return (
    <div className={`App ${themeClass}`}>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => {
          setShowAuthModal(false);
          setAuthModalMode('login'); // 모달 닫을 때 기본값으로 리셋
        }}
        onLogin={handleLogin}
        onRegister={handleRegister}
        onKakaoLogin={handleKakaoLogin}
        theme={state.theme}
        initialMode={authModalMode}
        onShowTermsModal={() => setShowTermsModal(true)}
        onShowPrivacyModal={() => setShowPrivacyModal(true)}
      />

      <ConsentModal
        isOpen={!!pendingSignupData}
        email={pendingSignupData?.email || ''}
        nickname={pendingSignupData?.nickname || ''}
        onAgree={completeSignup}
        onCancel={cancelSignup}
        theme={state.theme}
      />

      {isKakaoCallbackPending ? (
        <div
          className="kakao-login-loading"
          style={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: state.theme === 'dark' ? '#1a1a2e' : '#f0f4ff',
            color: 'var(--text-primary, #333)',
            gap: '1rem',
          }}
        >
          <div style={{ width: 32, height: 32, border: '3px solid #eee', borderTopColor: '#fee500', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <span>카카오 로그인 중...</span>
        </div>
      ) : showLandingPage ? (
        <LandingPage
          theme={state.theme}
          onGetStarted={() => {
            setAuthModalMode('register');
            setShowAuthModal(true);
          }}
          onLogin={() => {
            setAuthModalMode('login');
            setShowAuthModal(true);
          }}
        />
      ) : (
        <>
          <TopInfoBar
        theme={state.theme}
        toggleTheme={toggleTheme}
        isAutoMode={state.isAutoMode}
        isGameActive={state.gameState.isGameActive}
        timeLeft={state.timeLeft}
        formatTime={helpers.formatTime}
        user={user}
        formatBalance={helpers.formatBalance}
        showRankingModal={showRankingModal}
        onToggleRanking={handleToggleRanking}
        showTradingHistoryModal={showTradingHistoryModal}
        onToggleTradingHistory={handleToggleTradingHistory}
        showMyPageModal={showMyPageModal}
        onToggleMyPage={() => {
          if (!user) {
            setShowAuthModal(true);
            return;
          }
          if (showMyPageModal) {
            setShowMyPageModal(false);
          } else {
            // 마이페이지 열기
            setShowRankingModal(false);
            setShowTradingHistoryModal(false);
            setShowMyPageModal(true);
          }
        }}
        onLoginClick={() => setShowAuthModal(true)}
        onLogoutClick={handleLogout}
        onGoHome={handleGoHome}
        apiBaseUrl={API_BASE_URL}
        gameEndData={state.gameEndData}
      />

      {showRankingModal ? (
        <RankingPanel
          visible={showRankingModal}
          rankings={state.rankings}
          isLoading={state.isRankingLoading}
          error={state.rankingError}
          onRefresh={loadRankings}
          onClose={() => setShowRankingModal(false)}
          formatBalance={helpers.formatBalance}
          onAccountClick={handleAccountClick}
          currentPage={state.rankingCurrentPage}
          totalPages={state.rankingTotalPages}
          onPageChange={changeRankingPage}
          rankingsAsOf={(state as any).rankingsAsOf as string | null}
          myUserKey={user?.userKey ?? null}
          myRank={myRank}
          onViewMyRank={handleViewMyRank}
          myRankFlashToken={myRankFlashToken}
        />
      ) : showTradingHistoryModal ? (
        <TradingHistoryPanel
          visible={showTradingHistoryModal}
          onClose={() => {
            setShowTradingHistoryModal(false);
            setSelectedUserKey(undefined);
            setSelectedNickname(undefined);
            setTradingHistorySource(null);
          }}
          user={user}
          apiBaseUrl={API_BASE_URL}
          theme={state.theme}
          selectedUserKey={selectedUserKey}
          selectedNickname={selectedNickname}
          onBackToMyPage={
            tradingHistorySource === 'mypage'
              ? () => {
                  setShowTradingHistoryModal(false);
                  setShowMyPageModal(true);
                }
              : tradingHistorySource === 'ranking'
              ? () => {
                  setShowTradingHistoryModal(false);
                  setShowRankingModal(true);
                  setSelectedUserKey(undefined);
                  setTradingHistorySource(null);
                }
              : undefined
          }
        />
      ) : showMyPageModal ? (
        <MyPagePanel
          visible={showMyPageModal}
          onClose={() => {
            setShowMyPageModal(false);
            setHighlightPublicProfile(false);
          }}
          onNavigateToTradingHistory={() => {
            // 마이페이지 모달 닫고 매매내역 모달 열기
            setShowMyPageModal(false);
            setShowRankingModal(false); // 다른 모달도 확실히 닫기
            setShowTradingHistoryModal(true);
            setTradingHistorySource('mypage');
          }}
          theme={state.theme}
          onGoHome={handleGoHome}
          highlightPublicProfile={highlightPublicProfile}
          user={user}
        />
      ) : (
        <GameMain
          game={game}
          user={user}
          showRankingModal={showRankingModal}
          onLoginClick={() => setShowAuthModal(true)}
        />
      )}
        </>
      )}


      {/* 이용약관 모달 */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        theme={state.theme}
      />

      {/* 개인정보처리방침 모달 */}
      <PrivacyModal
        isOpen={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        theme={state.theme}
      />

      {/* 로그아웃 확인 팝업 */}
      {showLogoutConfirm && (
        <div className="logout-confirm-overlay">
          <div className="logout-confirm-popup">
            <div className="logout-icon">🚪</div>
            <h2>로그아웃</h2>
            <p className="logout-message">
              정말 로그아웃하시겠습니까?
              <br />
              <span className="logout-sub-message">
                다시 로그인하시면 계속 이용하실 수 있습니다.
              </span>
            </p>
            <div className="logout-buttons">
              <button
                onClick={handleLogoutCancel}
                className="logout-cancel-btn"
              >
                취소
              </button>
              <button
                onClick={handleLogoutConfirm}
                className="logout-confirm-btn"
              >
                로그아웃
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 비공개 프로필 팝업 (조회 대상) */}
      {showPrivateProfilePopup && (
        <div className="logout-confirm-overlay">
          <div className="logout-confirm-popup">
            <div className="logout-icon">🔒</div>
            <h2>비공개 프로필</h2>
            <p className="logout-message">
              이 사용자는 프로필을 비공개로 설정하여
              <br />
              <span className="logout-sub-message">
                매매내역을 볼 수 없습니다.
              </span>
            </p>
            <div className="logout-buttons">
              <button
                onClick={() => setShowPrivateProfilePopup(false)}
                className="logout-confirm-btn"
                style={{
                  background:
                    'linear-gradient(135deg, var(--accent-blue), #4a90e2)',
                  boxShadow: '0 4px 15px rgba(74, 144, 226, 0.3)',
                }}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 조회자 비공개 프로필 팝업 */}
      {showViewerPrivateProfilePopup && (
        <div className="logout-confirm-overlay">
          <div className="logout-confirm-popup">
            <div className="logout-icon">🔒</div>
            <h2>프로필 공개 필요</h2>
            <p className="logout-message">
              다른 사용자의 매매내역을 보려면
              <br />
              <span className="logout-sub-message">
                먼저 자신의 프로필을 공개로 설정해주세요.
              </span>
            </p>
            <div className="logout-buttons">
              <button
                onClick={() => {
                  setShowViewerPrivateProfilePopup(false);
                  setShowRankingModal(false);
                  setShowMyPageModal(true);
                  // 마이페이지가 열릴 때 프로필 공개 토글 강조
                  setTimeout(() => {
                    setHighlightPublicProfile(true);
                    // 애니메이션 종료 후 리셋 (약 2초 후)
                    setTimeout(() => {
                      setHighlightPublicProfile(false);
                    }, 2000);
                  }, 100);
                }}
                className="logout-confirm-btn"
                style={{
                  background:
                    'linear-gradient(135deg, var(--accent-green), #22c55e)',
                  boxShadow: '0 4px 15px rgba(34, 197, 94, 0.3)',
                }}
              >
                마이페이지로 이동
              </button>
              <button
                onClick={() => setShowViewerPrivateProfilePopup(false)}
                className="logout-cancel-btn"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {!isKakaoCallbackPending && (
      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-left">
              <div className="footer-brand">
                <img
                  src={headerLogo}
                  alt="차트 트레이너 로고"
                  className="footer-logo"
                />
                <span className="footer-brand-name">차트 트레이너</span>
                <span className="beta-badge">베타</span>
              </div>
              <div className="footer-info">
                <span className="footer-text">제작자: 마니후</span>
                <span className="footer-separator">|</span>
                <span className="footer-text">버전 v1.0 베타</span>
                <span className="footer-separator">|</span>
                <span className="footer-text">
                  문의 및 광고 제휴문의:{' '}
                  <a
                    href="mailto:mastermanihoo@gmail.com"
                    className="footer-email-link"
                    title="문의 및 광고 제휴문의"
                  >
                    mastermanihoo@gmail.com
                  </a>
                </span>
              </div>
            </div>
            <div className="footer-links">
              {/* 개인정보처리방침 링크 - 나중에 보이게 하려면 아래 주석을 해제하세요 */}
              {/* <button
                onClick={() => setShowPrivacyModal(true)}
                className="footer-link-btn"
                title="개인정보처리방침"
              >
                개인정보처리방침
              </button>
              <span className="footer-separator">|</span> */}
              {/* 개발자 블로그 링크 - 보이게 하려면 아래 주석 해제 */}
              {/* <a
                href="https://blog.naver.com/330529"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-blog-link"
                title="개발자 블로그"
              >
                <span className="naver-blog-icon">N</span>
                개발자 블로그
              </a> */}
            </div>
          </div>
          <div className="footer-legal-notice">
            <p className="footer-legal-text">
              ⚠️ 본 서비스는 금융투자업법에 따른 투자자문업, 투자일임업, 집합투자업 등에 해당하지 않으며, 
              실제 투자 조언이나 투자 권유를 제공하지 않습니다. 게임 내 AI 분석 결과는 교육 목적의 참고용이며, 
              실제 투자 결정에 사용해서는 안 됩니다. 실제 투자로 인한 손실에 대해서는 본 서비스가 책임을 지지 않습니다.
            </p>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
}

export default App;
